import { useRef, useState } from "react";
import { imageRoleLabel } from "@makit/shared";

import { CheckerThumb } from "../../components/primitives";

/** 순서를 바꿀 수 있는 사진 한 줄.
 *
 * **예시 작업과 서버 작업이 같은 모양으로 들어온다.** 예시는 실루엣(`obj`)을, 서버 작업은
 * 결과물 주소(`src`)를 들고 온다. 둘을 한 타입으로 두는 이유는 이 컴포넌트가 **순서를 바꾸는
 * 일**만 하기 때문이고, 무엇을 그릴지는 부르는 쪽이 정한다. */
export interface PhotoOrderRow {
  id: string;
  /** 줄에 적는 이름. 서버 작업은 원본 파일명, 없으면 부르는 쪽이 자리 문구를 만든다. */
  name: string;
  /** 예시 실루엣 클래스. 서버 작업에는 없다. */
  obj?: string;
  /** 결과물 주소. 아직 안 만든 장·못 만든 장에는 없다. */
  src?: string;
}

export interface PhotoOrderProps {
  photos: PhotoOrderRow[];
  onReorder: (next: PhotoOrderRow[]) => void;
  /** 프레임 예산 때문에 접은 나머지를 말하는 줄. 접은 것이 없으면 넘기지 않는다 —
   *  빈 줄을 그리면 셀러는 목록이 잘린 줄 읽는다. */
  foldNote?: string;
}

function move(list: PhotoOrderRow[], from: number, to: number): PhotoOrderRow[] {
  if (to < 0 || to >= list.length || from === to) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  if (!item) return list;
  next.splice(to, 0, item);
  return next;
}

/** 드래그만 두면 그 기능이 일부 셀러에게 없는 기능이 된다 — 키보드 경로를 함께 둔다. */
export function PhotoOrder({ photos, onReorder, foldNote }: PhotoOrderProps) {
  const [dragging, setDragging] = useState<number | null>(null);
  const gripsRef = useRef<HTMLButtonElement[]>([]);

  function moveTo(from: number, to: number) {
    const next = move(photos, from, to);
    if (next === photos) return;
    onReorder(next);
    window.requestAnimationFrame(() => gripsRef.current[to]?.focus());
  }

  return (
    <div className="orderlist">
      {photos.map((photo, i) => (
        <div
          key={photo.id}
          className={`orow${i === 0 ? " hero" : ""}${dragging === i ? " dragging" : ""}`}
          draggable
          onDragStart={() => setDragging(i)}
          onDragEnd={() => setDragging(null)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            if (dragging !== null) moveTo(dragging, i);
            setDragging(null);
          }}
        >
          <button
            type="button"
            className="grip"
            ref={(el) => {
              if (el) gripsRef.current[i] = el;
            }}
            aria-label={`순서 바꾸기: ${i + 1}번 ${photo.name}. 위아래 화살표로 옮깁니다`}
            onKeyDown={(e) => {
              if (e.key === "ArrowUp") {
                e.preventDefault();
                moveTo(i, i - 1);
              } else if (e.key === "ArrowDown") {
                e.preventDefault();
                moveTo(i, i + 1);
              }
            }}
          >
            ⠿
          </button>
          <CheckerThumb
            size={30}
            {...(photo.src ? { src: photo.src } : photo.obj ? { sample: photo.obj } : {})}
          />
          <span className="nm mono">{photo.name}</span>
          <span className="role">
            {i + 1} · {imageRoleLabel(i)}
          </span>
        </div>
      ))}

      {foldNote ? <div className="orow more">{foldNote}</div> : null}

      <p className="hint">
        끌어서 바꾸면 <b>왼쪽 미리보기가 바로 따라옵니다.</b> <b>맨 위가 대표컷</b>이고 대표이미지도 이 장으로
        나갑니다. 모델이 고르지 않는 이유가 여기 있습니다.
      </p>
    </div>
  );
}
